import React, { useEffect, useMemo, useState } from 'react';

import { useHelpBundle } from '@plasma/ui.application.app';
import { useResourceBundle, useTranslation } from '@plasma/ui.utils.i18n';

import { ESCALATION_LEVEL_LABEL_KEYS, EscalationLevel } from '../../constants/personnel';
import translations from './i18n';
import { AssociatedPersonnelProps } from './associated-personnel';
import help from './associated-personnel.help';
import { fetchAllDataGet_byApiName } from '../../services/gets/getdata';
import { apiUriPersonnelHttp } from '../../services/conf/apiConf';
import { sendAllDataPost_byApiNameAndData } from '../../services/posts/postdata';
import notification from '@plasma/ui.feedback.notification';

export interface PersonnelListItem {
  id: string;
  personnelName: string;
  personnelEmail: string;
  flagInternal: number;
  entityName?: string | null;
}

export interface AssociatedPersonnelListItem {
  personnelId: string;
  personnelName: string;
  personnelEmail: string;
  designation?: string;
  department?: string;
  flagEmail: boolean;
  escalationLevel: EscalationLevel;
  entityName?: string | null;
}

function useAssociatedPersonnelHook(props: AssociatedPersonnelProps) {
  useResourceBundle(translations, undefined, '4cc93710-3792-49a8-ad71-f655691cd26f');
  useHelpBundle(help);

  const { t } = useTranslation();

  const [cid, setcid] = useState<string | null>(null);
  const [personnel, setPersonnel] = useState<PersonnelListItem[]>([]);
  const [isMouseEntered, setisMouseEntered] = useState(false);
  const [click, setClick] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [active, setActive] = useState(true);
  const [sendMail, setsendMail] = useState<boolean>(true);
  const [remarks, setRemarks] = useState('');
  const [pid, setPid] = useState('');
  const [getAssocite, setGetAssociate] = useState<AssociatedPersonnelListItem[]>([]);

  const [companyId, setCompanyId] = useState<string | null>(null);
  const [initCompanyId, setInitComapnyId] = useState<string | null>(null);

  const [escalationValue, setescalationValue] = useState<EscalationLevel>(EscalationLevel.Low);

  // Memoized: a fresh array every render gives the Select dropdown an unstable options
  // identity, which can trigger an infinite update loop (React error #185) the instant the
  // dropdown opens - seen for real on All Personnel's dropdowns, fixed there the same way.
  const escalationOption = useMemo(
    () =>
      [EscalationLevel.Low, EscalationLevel.Medium, EscalationLevel.High].map((value) => ({
        display: t(ESCALATION_LEVEL_LABEL_KEYS[value]),
        value,
      })),
    [t],
  );

  useEffect(() => {
      if (!companyId) {
        const storedData = sessionStorage.getItem('companyID');
        if (storedData) {
          setCompanyId(storedData);
        }
      }
  setInitComapnyId(companyId);
  }, [companyId]);


  useEffect(()=>{
    if(initCompanyId){

      fetchAllDataGet_byApiName(`GetAllPersonnel?companyId=${initCompanyId}`, apiUriPersonnelHttp())
      .then((datam) => {
        setPersonnel(Array.isArray(datam) ? datam : []);
      })
      .catch((error) => {
        console.log('Failed to Fetch companies');
      });

      fetchAllDataGet_byApiName(`GetAssociatedPersonnel?companyId=${initCompanyId}`, apiUriPersonnelHttp())
      .then((res) => {
        setGetAssociate(Array.isArray(res) ? res : []);
        setcid(initCompanyId);
      })
      .catch((error) => {
        console.log('Failed to Fetch companies');
      });

    }
  },[initCompanyId])

  const HandleModalCancel = () => {
    setShowModal(false);
    setActive(true);
    setsendMail(true);
    setescalationValue(EscalationLevel.Low);
    setRemarks('');
  };

  const changeActive = (e) => {
    setActive((prevState) => !prevState);
  };

  const changeRemarks = (e) => {
    setRemarks(e.target.value);
  };

  // Drag-and-drop entry point: dropping a source-panel personnel card onto the "Associated"
  // panel selects it and opens the same modal the Associate button uses, so the required
  // Escalation Level / Send Email / Remarks metadata still gets entered either way.
  const handleDropToAssociate = (personnelId: string) => {
    setPid(personnelId);
    setShowModal(true);
  };

  const HandleModalOk = () => {

    const jsonDta = {
      companyId: initCompanyId,
      personnelId: pid,
      flagemail: sendMail,
      escalationLevel: escalationValue,
      isActive: active,
      remarks: remarks,
    };

    //console.log(jsonDta);

    sendAllDataPost_byApiNameAndData('mapPersonneltoCompany', jsonDta, apiUriPersonnelHttp()).then((response) => {
      console.log(response);
      setRemarks('');

       fetchAllDataGet_byApiName(`GetAssociatedPersonnel?companyId=${cid}`, apiUriPersonnelHttp())
        .then((res) => {
          setGetAssociate(Array.isArray(res) ? res : []);
        })
        .catch((error) => {
          console.log('Failed to Fetch companies');
        });

    });
    setClick(!click);
    setShowModal(false);
  };

  const performDeassociate = (personnelIdToRemove: string) => {
    if (cid !== '' && personnelIdToRemove !== '') {
      const jsonToDelete = {
        companyId: cid,
        personnelId: personnelIdToRemove,
        isActive: active,
        remarks: remarks,
      };

      sendAllDataPost_byApiNameAndData('unmapPersonnelToCompany', jsonToDelete, apiUriPersonnelHttp()).then((response) => {
        if (response) {
          fetchAllDataGet_byApiName(`GetAssociatedPersonnel?companyId=${cid}`, apiUriPersonnelHttp())
            .then((res) => {
              setGetAssociate(Array.isArray(res) ? res : []);
            })
            .catch((error) => {
              console.log('Failed to Fetch companies');
            });
        }
      });
    } else {
      notification.warning('Please select de-associated Personnel');
    }
  };

  // Drag-and-drop entry point: dropping an associated-panel item onto the "Remove" zone
  // de-associates it directly. Confirmed via a native prompt since drag is easy to trigger
  // by accident, unlike a deliberate button click.
  const handleDropToDeassociate = (person: AssociatedPersonnelListItem) => {
    if (window.confirm(`Remove ${person.personnelName} from this company?`)) {
      performDeassociate(person.personnelId);
    }
  };

  const handleEscalationLevelChange = (value: any) =>{
    setescalationValue(value);
  };

  const handleSendMail = (checked: boolean) => {
    setsendMail(checked);
  };


  return {
    changeRemarks,
    remarks,
    t,
    cid,
    personnel,
    isMouseEntered,
    setisMouseEntered,
    changeActive,
    click,
    setClick,
    setShowModal,
    showModal,
    HandleModalCancel,
    active,
    HandleModalOk,
    getAssocite,
    handleDropToAssociate,
    handleDropToDeassociate,
    escalationOption,
    escalationValue,
    sendMail,
    handleEscalationLevelChange,
    handleSendMail
  };
}

export default useAssociatedPersonnelHook;
